import { ChangeEvent, useCallback, useEffect, useState } from 'react';

import { Box, FormLabel, TextField, useTheme } from '@mui/material';
import { debounce } from '@npi/buddy-utils';
import { useField } from 'formik';
import { CountryCode } from 'libphonenumber-js';

import Loader from 'src/components/Loader/Loader';
import { useCountries, useValidatePhoneNumber } from 'src/hooks';
import { Country } from 'src/hooks/useCountries';

import ValidateButton from './components/ValidateButton';
import CountryDropDown from './CountryDropDown';
import { PhoneNumberFieldProps } from './types';
import {
    handleFormatNumberCountryChanged,
    handleFormatNumberInputChanged,
    handleInitialCountrySetup,
    validatePhoneNumber,
} from './utils';

/**
 * Renders a phone number input field with country selection and validation.
 * Handles input changes, country changes, and integrates with Formik for form state management.
 *
 * @param {PhoneNumberFieldProps} props - The properties for the phone number field component.
 * @returns {JSX.Element} The rendered phone number input field component.
 * @throws {Error} Throws an error if phone number validation fails.
 */
const PhoneNumberField = ({
    boxStyling,
    label = 'Mobile Number',
    placeholder = 'Enter Phone Number',
    searchText = 'Validate',
    formikFieldName,
    dataTestId = formikFieldName,
    disabled,
}: PhoneNumberFieldProps) => {
    const [field, meta, helpers] = useField(formikFieldName);

    const { value, initialValue } = meta;
    const { setValue, setError } = helpers;
    const { onBlur } = field;

    const { countries: countryData, loadingCountries } = useCountries();
    const { onValidatePhoneNumber, validatingPhoneNumber } = useValidatePhoneNumber();

    const [countries, setCountries] = useState<Country[]>();
    const [selectedCountryName, setSelectedCountryName] = useState('Ireland');
    const [areaCode, setAreaCode] = useState<CountryCode | string>('IE');

    const { palette } = useTheme();
    const bgColour = meta.touched && meta.error ? palette.error.main : palette.border.light;

    useEffect(() => {
        handleInitialCountrySetup({
            initialValue,
            countryData,
            loadingCountries,
            setCountries,
            setSelectedCountryName,
            setAreaCode,
        });
    }, [countryData, loadingCountries, initialValue]);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue, setValue]);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputNumber = event.target.value;
        setValue(inputNumber);
    };

    const handleCountryChanged = useCallback(() => {
        const debouncedFunction = debounce(() => {
            handleFormatNumberCountryChanged({
                value,
                countryData,
                selectedCountryName,
                setValue,
            });
        }, 1000);

        debouncedFunction();
        return debouncedFunction;
    }, [value, countryData, selectedCountryName, setValue]);

    const handleInputChanged = useCallback(() => {
        const debouncedFunction = debounce(() => {
            handleFormatNumberInputChanged({
                value,
                initialValue,
                countryData,
                selectedCountryName,
                setValue,
                setAreaCode,
                areaCode,
                setSelectedCountryName,
            });
        }, 1000);

        debouncedFunction();
        return debouncedFunction;
    }, [
        value,
        countryData,
        selectedCountryName,
        setValue,
        areaCode,
        setAreaCode,
        setSelectedCountryName,
    ]);

    useEffect(() => {
        const debouncedCountryHandler = handleCountryChanged();
        return () => {
            debouncedCountryHandler.cancel();
        };
    }, [selectedCountryName]);

    useEffect(() => {
        const debouncedInputHandler = handleInputChanged();
        return () => {
            debouncedInputHandler.cancel();
        };
    }, [value]);

    const validateNumber = async () => {
        return await validatePhoneNumber({
            value,
            onValidatePhoneNumber,
            setError,
            setValue,
        });
    };

    return (
        <>
            {(loadingCountries || validatingPhoneNumber) && <Loader />}
            <Box display="flex" flexDirection="column" flex={0.49} {...boxStyling}>
                <FormLabel sx={{ marginBottom: '8px', color: '#000', fontWeight: 600 }}>
                    {label}
                </FormLabel>
                <Box
                    className={`search-input-box ${
                        meta.touched && meta.error ? 'input-error' : ''
                    }`}
                    display="flex"
                    flexDirection="row"
                    height="51px"
                    borderRadius="8px"
                    overflow="hidden"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    {countries && !loadingCountries && (
                        <CountryDropDown
                            countries={countries}
                            selectedCountryName={selectedCountryName || 'Ireland'} // Fallback to 'Ireland'
                            setSelectedCountryName={setSelectedCountryName}
                            setAreaCode={setAreaCode}
                            disabled={disabled}
                        />
                    )}
                    <TextField
                        name={field.name}
                        value={value}
                        onChange={handleInputChange}
                        placeholder={placeholder}
                        sx={{
                            '& input[type=number]': {
                                '-moz-appearance': 'textfield',
                            },
                            '& input[type=number]::-webkit-outer-spin-button': {
                                '-webkit-appearance': 'none',
                                margin: 0,
                            },
                            '& input[type=number]::-webkit-inner-spin-button': {
                                '-webkit-appearance': 'none',
                                margin: 0,
                            },
                        }}
                        autoComplete="off"
                        InputProps={{
                            'data-testid': `${dataTestId}-input`,
                            disableUnderline: true,
                            onBlur,
                        }}
                        variant="standard"
                        disabled={disabled}
                    />
                    <ValidateButton
                        onClick={validateNumber}
                        disabled={!value || disabled}
                        searchText={searchText}
                        dataTestId={dataTestId}
                        sx={{ bgcolor: bgColour }}
                    />
                </Box>
                {meta.touched && meta.error && (
                    <label className="input-base-field-error" style={{ position: 'relative' }}>
                        {meta.error}
                    </label>
                )}
            </Box>
        </>
    );
};

export default PhoneNumberField;
